import React, { useState, useEffect } from "react";
import {
  Box,
  FormControl,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  Button,
} from "@mui/material";
import { useUser } from "../../pages/login/UserContextPersistent";
import DashboardMap from "./DashboardMap";
import LiveIcon from "../../images/dashboard/map_live.svg";
import "./Dashboard.css";
import { useTranslation } from "react-i18next";
import {
  getAllStops,
  getAllRoutes,
  getAllVehicles,
  handleApiError,
} from "./dashboardAPI";
import { useNavigate } from "react-router";
import { useUserInfo } from "../../hooks/useUserInfo";
import PerformanceAnalytics from "./PerformanceAnalytics";

const getVehiclesForRoutes = (allVehicles = [], routes = []) => {
  const routeIds = new Set(
    routes.map((route) => route?.id?.toString()).filter(Boolean),
  );
  const routeVehicleIds = new Set(
    routes.flatMap((route) =>
      (route?.vehicles || [])
        .map((vehicle) => vehicle?.device_id?.toString())
        .filter(Boolean),
    ),
  );

  return allVehicles.filter((vehicle) => {
    const deviceId = vehicle?.device_id?.toString();
    const routeId = vehicle?.routeId?.toString();

    return (
      (routeId && routeIds.has(routeId)) ||
      (deviceId && routeVehicleIds.has(deviceId))
    );
  });
};

export default function DashboardMapFilter({ filterBy = "routes", dashboard }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useUser();

  const { orgId } = useUserInfo();

  const [isLoading, setIsLoading] = useState(true);
  // const [selectedRoutes, setSelectedRoutes] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [data, setData] = useState({
    vehicles: [],
    routes: [],
    stops: [],
    users: [],
  });

  // Filtered data state
  const [filteredStops, setFilteredStops] = useState([]);
  const [filteredRoutes, setFilteredRoutes] = useState([]);
  const [filteredVehicles, setFilteredVehicles] = useState([]);

  const handleItemChange = (event) => {
    const selectedValues = event.target.value;
    setSelectedItems(selectedValues);
    filterDataByItems(selectedValues);
  };

  const filterDataByItems = (selectedItemIds) => {
    if (selectedItemIds.length === 0) {
      // If no items selected, show all data
      setFilteredRoutes(data.routes);
      setFilteredStops(data.stops);
      setFilteredVehicles(data.vehicles);
      return;
    }

    if (filterBy === "routes") {
      // Existing route filtering logic
      const routes = data.routes.filter((route) =>
        selectedItemIds.includes(route.id.toString())
      );
      setFilteredRoutes(routes);

      const stops = data.stops.filter((stop) => {
        return selectedItemIds.some((routeId) => {
          const selectedRouteObj = data.routes.find(
            (r) => r.id.toString() === routeId
          );
          return selectedRouteObj?.routeCoordinates?.some(
            (coord) =>
              coord.latitude === stop.latitude &&
              coord.longitude === stop.longitude
          );
        });
      });
      setFilteredStops(stops);

      const vehicles = getVehiclesForRoutes(data.vehicles, routes);
      setFilteredVehicles(vehicles);
    } else if (filterBy === "stops") {
      // Filter by stops
      const stops = data.stops.filter((stop) =>
        selectedItemIds.includes(stop.id.toString())
      );
      setFilteredStops(stops);

      // Find routes that contain these stops
      const routes = data.routes.filter((route) => {
        return route.routeCoordinates?.some((coord) =>
          selectedItemIds.some((stopId) => {
            const selectedStop = data.stops.find(
              (s) => s.id.toString() === stopId
            );
            return (
              selectedStop &&
              coord.latitude === selectedStop.latitude &&
              coord.longitude === selectedStop.longitude
            );
          })
        );
      });
      setFilteredRoutes(routes);

      // Find vehicles on routes that contain these stops
      const vehicles = getVehiclesForRoutes(data.vehicles, routes);
      setFilteredVehicles(vehicles);
    } else if (filterBy === "vehicles") {
      // Filter by vehicles
      const vehicles = data.vehicles.filter((vehicle) =>
        selectedItemIds.includes(
          vehicle.id?.toString() || vehicle.device_id?.toString()
        )
      );

      setFilteredVehicles(vehicles);

      // Find routes for these vehicles
      const routeIds = vehicles
        .map((v) => v.routeId?.toString())
        .filter(Boolean);
      const routes = data.routes.filter((route) =>
        routeIds.includes(route.id.toString())
      );
      setFilteredRoutes(routes);

      // Find stops on these routes
      const stops = data.stops.filter((stop) => {
        return routes.some((route) =>
          route.routeCoordinates?.some(
            (coord) =>
              coord.latitude === stop.latitude &&
              coord.longitude === stop.longitude
          )
        );
      });
      setFilteredStops(stops);
    }
  };

  const getCurrentDataArray = () => {
    switch (filterBy) {
      case "stops":
        return data.stops;
      case "vehicles":
        return data.vehicles;
      case "routes":
      default:
        return data.routes;
    }
  };

  const getItemDisplayName = (item) => {
    switch (filterBy) {
      case "stops":
        return item.name || `Stop ${item.id}`;
      case "vehicles":
        return item.name || `Vehicle ${item.id || item.device_id}`;
      case "routes":
      default:
        return item.name || `Route ${item.id}`;
    }
  };

  const getItemId = (item) => {
    switch (filterBy) {
      case "vehicles":
        return (item.id || item.device_id)?.toString();
      default:
        return item.id?.toString();
    }
  };

  // Helper function to get placeholder text
  const getPlaceholderText = () => {
    switch (filterBy) {
      case "stops":
        return t("Select Stops");
      case "vehicles":
        return t("Select Vehicles");
      case "routes":
      default:
        return t("Select Routes");
    }
  };

  // Helper function to get selected count text
  const getSelectedCountText = (count) => {
    switch (filterBy) {
      case "stops":
        return `${count} stops selected`;
      case "vehicles":
        return `${count} vehicles selected`;
      case "routes":
      default:
        return `${count} routes selected`;
    }
  };

  // Fetch initial data
  const fetchInitialData = async () => {
    setIsLoading(true);
    try {
      const [vehicles, routes, stops] = await Promise.all([
        getAllVehicles(),
        getAllRoutes(),
        getAllStops(),
      ]);

      const initialData = {
        vehicles,
        routes,
        stops,
        users: [], // If you need users data, add it here
      };

      setData(initialData);

      // Initialize filtered data with all data
      setFilteredVehicles(vehicles);
      setFilteredRoutes(routes);
      setFilteredStops(stops);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const handleViewFull = () => {
    navigate(`/independent/dashboard?org=${orgId}`);
  };

  return (
    <Box sx={{ position: "relative", height: "100%" }}>
      {/* Header container with Live indicator and Select box */}
      <Box
        sx={{
          position: "absolute",
          top: 50,
          right: 50,
          left: 50,
          zIndex: 1,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        {/* Live Map indicator */}
        <Box
          sx={{
            backgroundColor: "white",
            padding: "8px 16px",
            borderRadius: 1,
            boxShadow: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <img
            src={LiveIcon}
            alt="Live Map"
            style={{
              width: 20,
              height: 20,
              animation: "pulse 1.5s infinite",
            }}
          />
          <span style={{ fontSize: "12px" }}>{t("liveMap")}</span>
        </Box>

        {/* Select Box */}
        <Box
          sx={{
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            overflow: "hidden",
          }}
        >
          {dashboard && (
            <Button
              sx={{
                fontFamily: "Roboto",
                fontWeight: 600,
                fontSize: "16px",
                color: "#FFFFFF",
                padding: "0px 16px",
                marginRight: "8px",
                minWidth: "80px",
                height: "40px",
                borderRadius: "8px",
                textTransform: "none",
                backgroundColor: "#20A8E0",
                borderRight: "1px solid #e0e0e0",
                "&:hover": {
                  backgroundColor: "#20A8E0",
                },
              }}
              onClick={handleViewFull}
            >
              {t("Site View")}
            </Button>
          )}
          <FormControl size="small" sx={{ width: 200 }}>
            <Select
              multiple
              displayEmpty
              value={selectedItems}
              onChange={handleItemChange}
              sx={{
                backgroundColor: "white",
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return <em>{getPlaceholderText()}</em>;
                }
                return getSelectedCountText(selected.length);
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 200,
                    overflowY: "auto",
                    borderRadius: "8px",
                    "&::-webkit-scrollbar": {
                      width: "4px",
                      backgroundColor: " #B7BFCC",
                    },
                    "&::-webkit-scrollbar-thumb": {
                      backgroundColor: " #647490",
                      borderRadius: "4px",
                    },
                    "&::-webkit-scrollbar-thumb:hover": {
                      backgroundColor: "#555",
                    },
                  },
                },
              }}
            >
              <MenuItem disabled value="">
                <em>{getPlaceholderText()}</em>
              </MenuItem>
              {getCurrentDataArray()?.map((item) => (
                <MenuItem
                  key={getItemId(item)}
                  value={getItemId(item)}
                  sx={{
                    fontSize: "10px",
                    padding: "6px 8px",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  <Checkbox
                    checked={selectedItems.includes(getItemId(item))}
                    size="small"
                    sx={{ padding: "4px" }}
                  />
                  <ListItemText
                    primary={getItemDisplayName(item)}
                    sx={{
                      fontSize: "10px",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      maxWidth: "150px",
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      {/* Map Container */}
      <Box sx={{ height: "100%", padding: "0px" }}>
        {isLoading ? (
          <div style={{ padding: "40px" }}>
            <div height={400} width="100%" />
          </div>
        ) : (
          <DashboardMap
            stops={filteredStops}
            routes={filteredRoutes}
            vehicles={filteredVehicles}
          />
        )}
      </Box>

      {dashboard && state?.features?.some(feature => feature.api_path === '/api/managedata/analytics') && (
        <Box sx={{ mt: 4 }}>
          <PerformanceAnalytics vehiclesData={filteredVehicles} />
        </Box>
      )}
    </Box>
  );
}
